import {useEffect, useRef} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {twMerge} from 'tailwind-merge'
import BoxSection from './BoxSection'
import useDialog from '../../../hooks/useDialog'
import useAuth from '../../../hooks/useAuth'
import {useCurrentWorkspaceCreditsQuery, useCurrentWorkspaceDetailQuery} from '../../../hooks/queries'
import {paymentsQueries} from '../../../services/payments'
import {isOneTimePayConfigured} from '../../../utils/portone'
import CreditPackModal, {CREDIT_PACK_RETURN_PARAM} from './CreditPackModal'

type Props = {
    className?: string
}

export default function CreditPackSection({className}: Props) {
    const dialog = useDialog()
    const {workspaceId: workspaceIdParam} = useParams()
    const workspaceId = Number(workspaceIdParam)
    const {user} = useAuth()
    const {data: workspace} = useCurrentWorkspaceDetailQuery()
    const {data: credits} = useCurrentWorkspaceCreditsQuery()

    // 주문 생성 · 확정은 소유자만 된다(구성원은 403). role 은 백엔드 enum 이름 그대로 소문자("owner")로 온다 —
    // 대문자와 비교하면 모든 소유자가 비소유자로 판정돼 판매 목록 조회도 구매 버튼도 사라진다. 대소문자를 가리지 않는다
    const isOwner = user?.workspaceList?.find((item) => item.id === workspaceId)?.role?.toLowerCase() === 'owner'
    // 단건 상점 설정이 없으면 정기결제 상점으로 대체하지 않고 구매를 막는다(정책 7절 · 명세 5-6)
    const payConfigured = isOneTimePayConfigured()
    const {data: catalog} = useQuery({
        ...paymentsQueries.creditPacks(workspaceId),
        enabled: Boolean(workspaceId) && isOwner && payConfigured,
    })
    // 카드 정기결제로 구독 중인 유료 워크스페이스에만 보인다. 아니면 비활성이 아니라 숨긴다(정책 7절 · 5.4)
    const canPurchase = isOwner && payConfigured && catalog?.canPurchase === true

    useCreditPackPaymentReturn(workspaceId)

    const isPause = workspace?.subscription?.status === 'PAUSE'
    let available = isPause ? 0 : credits?.available ?? 0
    if (available < 0) available = 0

    return (
        <BoxSection className={twMerge('flex flex-col', className)} title="크레딧">
            <div className="text-gray-2 mt-[13px] text-[22px] font-bold">
                {available.toLocaleString()}
                <span className="text-gray-21 ml-[6px] text-[13px] font-normal">크레딧</span>
            </div>

            {canPurchase ? (
                <button
                    type="button"
                    onClick={() => dialog.open(<CreditPackModal workspaceId={workspaceId}/>)}
                    className="border-gray-4 text-gray-2 hover:bg-gray-6 mt-auto flex h-[40px] w-fit items-center justify-center gap-[5px] rounded-[8px] border px-[20px] text-[14px] font-normal transition-colors"
                >
                    크레딧 팩 구매
                </button>
            ) : null}
        </BoxSection>
    )
}

/**
 * 모바일 결제 복귀 — 결제 화면으로 페이지가 이동했다가 m_redirect_url 로 돌아오면, 도착한 페이지에서
 * 크레딧 팩 모달을 3단계(결제 결과)로 다시 열어 같은 확정을 부른다. 질의값은 바로 지워 새로고침 · 뒤로가기로
 * 모달이 다시 열리지 않게 한다(다시 불러도 지급은 한 번이다).
 */
function useCreditPackPaymentReturn(workspaceId: number) {
    const dialog = useDialog()
    const [searchParams, setSearchParams] = useSearchParams()
    const handledRef = useRef(false)

    useEffect(() => {
        if (handledRef.current || !workspaceId || searchParams.get(CREDIT_PACK_RETURN_PARAM) !== '1') return
        handledRef.current = true

        const merchantUid = searchParams.get('merchant_uid')
        const impUid = searchParams.get('imp_uid')
        const errorMsg = searchParams.get('error_msg')

        const next = new URLSearchParams(searchParams)
        ;[CREDIT_PACK_RETURN_PARAM, 'imp_uid', 'merchant_uid', 'imp_success', 'error_code', 'error_msg'].forEach(
            (key) => next.delete(key),
        )
        setSearchParams(next, {replace: true})

        if (!merchantUid) return
        dialog.open(<CreditPackModal workspaceId={workspaceId} paymentReturn={{merchantUid, impUid, errorMsg}}/>)
    }, [dialog, searchParams, setSearchParams, workspaceId])
}
